import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";
import { can } from "@/lib/permissions";

const configuredUrl = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
const API = (() => {
  if (!configuredUrl) return "/api";
  try {
    const configHost = new URL(configuredUrl).hostname;
    if (typeof window !== "undefined" && window.location.hostname !== configHost) {
      return "/api";
    }
  } catch (err) { console.error("API URL parse:", err); }
  return `${configuredUrl}/api`;
})();

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(data);
    } catch { /* not authenticated - expected */
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (username, password) => {
    const { data } = await axios.post(`${API}/auth/login`, { username, password }, { withCredentials: true });
    setUser(data);
    return data;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      // Logout should clear local state even if the server call fails
      console.error("Logout request failed:", error);
    }
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, checkAuth, can: (module, action) => can(user, module, action) }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}

export { API };
